import * as THREE from "three";
import { Scene } from "../core/Scene";
import { Ease, Tweener } from "../core/Tween";
import { CharacterModel } from "../render/CharacterModel";
import { MonsterModel } from "../render/MonsterModel";
import { Battle, type BattleEvent, type BattleOutcome, type Command } from "../rpg/Battle";
import type { Hero } from "../rpg/Hero";
import type { Monster } from "../rpg/Monster";
import type { SkillDef } from "../rpg/Skills";
import { Combatant } from "../rpg/Stats";
import type { World } from "../rpg/World";
import { shade } from "../world/Tiles";
import type { MenuItem } from "../ui/Menu";
import { GameWindow } from "../ui/Window";

const HERO_Z = 2.6;
const MONSTER_Z = -1.6;

/**
 * Look of the arena. The field scene passes down whatever the party was
 * standing on, so a fight in a cave is lit like a cave and a fight on the moor
 * is lit like the moor.
 */
export interface BattleEnvironment {
  /** Colour of the ground disc. */
  ground?: number;
  /** Sky and fog colour. */
  sky?: number;
  ambient?: number;
  sunColor?: number;
  sunIntensity?: number;
}

interface MonsterView {
  monster: Monster;
  model: MonsterModel;
  home: THREE.Vector3;
}

interface HeroView {
  hero: Hero;
  model: CharacterModel;
  home: THREE.Vector3;
}

/**
 * Turn-based battle, shown over the shoulder: the party stands in the
 * foreground with their backs to us, the monsters face us across the field.
 *
 * The scene owns no rules — it collects commands, hands them to `Battle`, and
 * animates the events that come back.
 */
export class BattleScene extends Scene {
  /** Resolves once the battle is over and the scene has popped itself. */
  readonly finished: Promise<BattleOutcome>;

  private resolveFinished!: (outcome: BattleOutcome) => void;
  /** Created in `enter`, once the scene has a game (and therefore an RNG). */
  private battle!: Battle;
  private tweener = new Tweener();
  private monsterViews: MonsterView[] = [];
  private heroViews: HeroView[] = [];
  private statusWindow!: GameWindow;
  private targetMarker!: THREE.Mesh;
  private popups: { el: HTMLElement; position: THREE.Vector3; life: number }[] = [];
  private started = false;
  private shake = 0;
  private orbit = 0;
  private cameraBase = new THREE.Vector3(0, 4.0, 7.9);
  private lookTarget = new THREE.Vector3(0, 1.0, 0.2);

  constructor(
    private world: World,
    private monsters: Monster[],
    private environment: BattleEnvironment = {},
  ) {
    super();
    this.camera = new THREE.PerspectiveCamera(50, 1, 0.1, 120);
    this.finished = new Promise((resolve) => {
      this.resolveFinished = resolve;
    });
  }

  async enter(): Promise<void> {
    this.battle = new Battle(this.world.party, this.monsters, {
      rng: this.game.rng,
      skills: this.world.skills,
      items: this.world.items,
    });
    this.buildScene();
    this.statusWindow = this.game.ui.window("", "ds-battle-status");
    this.refreshStatus();
    this.game.ui.setHint("Z  confirm   ·   X  back");
    if (this.world.songs.battle) this.game.audio.playSong(this.world.songs.battle);
  }

  exit(): void {
    this.tweener.clear();
    for (const view of this.monsterViews) view.model.dispose();
    for (const view of this.heroViews) view.model.dispose();
    for (const popup of this.popups) popup.el.remove();
    this.popups = [];
    this.statusWindow.destroy();
    this.disposeGraph();
    this.scene.clear();
    this.game.ui.setHint("");
    this.game.audio.stopSong();
  }

  // ------------------------------------------------------------------ setup

  private buildScene(): void {
    const env = this.environment;
    // Darkened so the fight always reads as its own place, wherever it started.
    const backdrop = shade(env.sky ?? 0x0c1024, 0.55);
    this.scene.background = new THREE.Color(backdrop);
    this.scene.fog = new THREE.Fog(backdrop, 12, 26);

    const ground = new THREE.Mesh(
      new THREE.CircleGeometry(14, 32),
      new THREE.MeshLambertMaterial({ color: env.ground ?? 0x3f7a3f }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.scene.add(ground);

    this.scene.add(new THREE.HemisphereLight(env.sky ?? 0x8899ff, 0x202028, env.ambient ?? 1.0));
    const key = new THREE.DirectionalLight(env.sunColor ?? 0xffe8c0, env.sunIntensity ?? 1.6);
    key.position.set(4, 9, 6);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    const cam = key.shadow.camera;
    cam.left = -8;
    cam.right = 8;
    cam.top = 8;
    cam.bottom = -8;
    cam.far = 30;
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(0x6688ff, 0.5);
    rim.position.set(-5, 4, -6);
    this.scene.add(rim);

    // Monsters, spread across the far side facing the party.
    const monsters = this.battle.monsters;
    monsters.forEach((monster, i) => {
      const model = new MonsterModel(monster.def.appearance);
      const spread = 1.9;
      const x = (i - (monsters.length - 1) / 2) * spread;
      const z = MONSTER_Z - (i % 2) * 0.7;
      model.object3D.position.set(x, 0, z);
      this.scene.add(model.object3D);
      this.monsterViews.push({ monster, model, home: new THREE.Vector3(x, 0, z) });
    });

    // Party in the foreground, seen from behind.
    const heroes = this.battle.heroes;
    heroes.forEach((hero, i) => {
      const model = new CharacterModel(hero.appearance);
      const x = (i - (heroes.length - 1) / 2) * 1.3;
      const z = HERO_Z + (i % 2) * 0.5;
      model.object3D.position.set(x, 0, z);
      model.object3D.rotation.y = Math.PI;
      model.object3D.scale.setScalar((hero.appearance.scale ?? 1) * 1.15);
      this.scene.add(model.object3D);
      this.heroViews.push({ hero, model, home: new THREE.Vector3(x, 0, z) });
    });

    const marker = new THREE.Mesh(
      new THREE.ConeGeometry(0.16, 0.34, 4),
      new THREE.MeshBasicMaterial({ color: 0xffd23f }),
    );
    marker.rotation.x = Math.PI;
    marker.visible = false;
    this.targetMarker = marker;
    this.scene.add(marker);

    this.camera.position.copy(this.cameraBase);
    this.camera.lookAt(this.lookTarget);
  }

  // ------------------------------------------------------------------ frame

  update(dt: number): void {
    if (!this.started) {
      this.started = true;
      void this.run();
    }

    this.game.audio.update(dt);
    this.tweener.update(dt);

    for (const view of this.monsterViews) view.model.update(dt);
    for (const view of this.heroViews) view.model.update(dt);

    if (this.targetMarker.visible) {
      this.targetMarker.position.y += Math.sin(performance.now() / 180) * 0.002;
      this.targetMarker.rotation.y += dt * 2;
    }

    // A slow drift around the arena keeps the shot alive between turns without
    // ever losing the party or making the menus hard to read.
    this.orbit += dt * 0.09;
    const sway = Math.sin(this.orbit) * 0.22;
    this.camera.position.set(
      this.cameraBase.x + Math.sin(sway) * this.cameraBase.z,
      this.cameraBase.y + Math.sin(this.orbit * 0.7) * 0.25,
      this.cameraBase.z * Math.cos(sway),
    );
    if (this.shake > 0) {
      this.shake = Math.max(0, this.shake - dt * 3);
      this.camera.position.x += (Math.random() - 0.5) * this.shake;
      this.camera.position.y += (Math.random() - 0.5) * this.shake;
    }
    this.camera.lookAt(this.lookTarget);

    this.updatePopups(dt);
  }

  // ------------------------------------------------------------- main loop

  private async run(): Promise<void> {
    await this.game.ui.message.show(this.battle.encounterText(), { autoAdvance: 1.1 });

    while (this.battle.outcome === "ongoing") {
      // Clear the text window while the player is choosing; an empty black bar
      // under the menus looks like a bug.
      this.game.ui.message.hide();
      const commands = await this.collectCommands();
      if (this.battle.outcome !== "ongoing") break;

      const events = this.battle.resolveRound(commands);
      for (const event of events) await this.playEvent(event);
      this.refreshStatus();
    }

    await this.conclude();
  }

  private async conclude(): Promise<void> {
    const outcome = this.battle.outcome;
    this.game.audio.stopSong();

    if (outcome === "win") {
      this.game.audio.play("victory");
      await this.tweener.wait(0.4);
    } else if (outcome === "fled") {
      await this.tweener.wait(0.2);
    } else {
      this.game.audio.play("defeat");
      await this.tweener.wait(0.5);
    }

    this.game.ui.message.hide();
    await this.game.pop();
    this.resolveFinished(outcome);
  }

  // ------------------------------------------------------- command entry

  private async collectCommands(): Promise<Map<Hero, Command>> {
    const commands = new Map<Hero, Command>();
    const heroes = this.battle.livingHeroes;
    let index = 0;

    while (index < heroes.length) {
      const hero = heroes[index];
      this.highlightActor(hero);
      const command = await this.chooseCommand(hero);

      if (command === null) {
        // Cancelling steps back to the previous party member.
        if (index === 0) continue;
        index--;
        commands.delete(heroes[index]);
        continue;
      }

      commands.set(hero, command);
      index++;
    }

    this.highlightActor(null);
    return commands;
  }

  private async chooseCommand(hero: Hero): Promise<Command | null> {
    const anchor = { right: "6%", bottom: "26%" };
    const choice = await this.game.ui.menu().open({
      title: hero.name,
      items: [
        { label: "Fight" },
        { label: "Spell", disabled: hero.skills.length === 0 },
        { label: "Item", disabled: this.usableItems().length === 0 },
        { label: "Defend" },
        { label: "Run" },
      ],
      anchor,
      cancelable: true,
    });

    switch (choice) {
      case 0: {
        const target = await this.chooseMonster();
        return target ? { kind: "attack", target } : this.chooseCommand(hero);
      }
      case 1: {
        const skill = await this.chooseSkill(hero);
        if (!skill) return this.chooseCommand(hero);
        const target = await this.chooseSkillTarget(skill);
        if (target === undefined) return this.chooseCommand(hero);
        return { kind: "skill", skill: skill.id, target: target ?? undefined };
      }
      case 2: {
        const items = this.usableItems();
        const index = await this.game.ui.menu().open({
          title: "Items",
          items: items.map((i) => ({ label: i.name, detail: `x${this.world.party.countItem(i.id)}` })),
          anchor: { right: "26%", bottom: "26%" },
        });
        if (index === null) return this.chooseCommand(hero);
        const item = items[index];
        const skill = item.use ? this.world.skills.get(item.use) : null;
        const target = skill ? await this.chooseSkillTarget(skill) : null;
        if (target === undefined) return this.chooseCommand(hero);
        return { kind: "item", item: item.id, target: target ?? undefined };
      }
      case 3:
        return { kind: "defend" };
      case 4:
        return { kind: "flee" };
      default:
        return null;
    }
  }

  private usableItems() {
    return this.world.party.items
      .map((slot) => this.world.items.get(slot.id))
      .filter((def) => def.use && (this.world.skills.get(def.use).battle ?? true));
  }

  private async chooseSkill(hero: Hero): Promise<SkillDef | null> {
    const skills = hero.skills
      .map((id) => this.world.skills.get(id))
      .filter((skill) => skill.battle ?? true);
    if (skills.length === 0) return null;

    const items: MenuItem[] = skills.map((skill) => ({
      label: skill.name,
      detail: `${skill.mp} MP`,
      disabled: hero.mp < skill.mp,
    }));

    const index = await this.game.ui.menu().open({
      title: "Spells",
      items,
      anchor: { right: "26%", bottom: "26%" },
    });
    return index === null ? null : skills[index];
  }

  /** Returns the target, null for "no target needed", or undefined if cancelled. */
  private async chooseSkillTarget(skill: SkillDef): Promise<Combatant | null | undefined> {
    switch (skill.target) {
      case "enemy": {
        const target = await this.chooseMonster();
        return target ?? undefined;
      }
      case "ally": {
        const members = this.world.party.members;
        if (members.length === 1) return members[0];
        const index = await this.game.ui.menu().open({
          title: "Who?",
          items: members.map((m) => ({ label: m.name, detail: `${m.hp}/${m.maxHp}` })),
          anchor: { left: "50%", bottom: "26%", transform: "translateX(-50%)" },
        });
        return index === null ? undefined : members[index];
      }
      default:
        return null;
    }
  }

  private async chooseMonster(): Promise<Monster | null> {
    const alive = this.monsterViews.filter((v) => v.monster.active);
    if (alive.length === 0) return null;
    if (alive.length === 1) return alive[0].monster;

    const index = await this.game.ui.menu().open({
      title: "Which one?",
      items: alive.map((v) => ({ label: v.monster.name })),
      anchor: { left: "50%", bottom: "26%", transform: "translateX(-50%)" },
      onHighlight: (i) => this.pointAt(alive[i]),
    });
    this.targetMarker.visible = false;
    return index === null ? null : alive[index].monster;
  }

  private pointAt(view: MonsterView): void {
    const scale = view.monster.def.appearance.scale ?? 1;
    this.targetMarker.visible = true;
    this.targetMarker.position.set(
      view.model.object3D.position.x,
      view.model.height * scale + 0.28,
      view.model.object3D.position.z,
    );
  }

  // ------------------------------------------------------ event playback

  private async playEvent(event: BattleEvent): Promise<void> {
    switch (event.type) {
      case "attack": {
        await this.animateApproach(event.actor, event.target);
        this.game.audio.play(event.missed ? "miss" : "attack");
        if (!event.missed) {
          this.hitReaction(event.target, event.damage, event.critical);
        }
        await this.say(event.text);
        break;
      }
      case "skill": {
        this.game.audio.play(event.skill.sound ?? "spell");
        await this.castFlare(event.actor, event.skill);
        await this.say(event.text);
        break;
      }
      case "item":
        this.game.audio.play("confirm");
        await this.say(event.text);
        break;
      case "damage":
        this.hitReaction(event.target, event.amount, false);
        if (event.text) await this.say(event.text);
        break;
      case "heal":
        this.game.audio.play("heal");
        this.popup(event.target, `+${event.amount}`, "#7dff9b");
        await this.say(event.text);
        break;
      case "defeated":
        await this.animateDefeat(event.target);
        await this.say(event.text);
        break;
      case "levelup":
        this.game.audio.play("levelup");
        await this.say(event.text);
        break;
      case "result":
        if (event.text) await this.say(event.text);
        break;
      case "flee":
        this.game.audio.play(event.success ? "confirm" : "bump");
        if (event.success && event.actor !== this.world.party.leader) {
          const view = this.viewOfMonster(event.actor);
          if (view) await this.tweener.to(view.model.object3D.position, { z: -14 }, 0.4, Ease.quadIn);
        }
        await this.say(event.text);
        break;
      default:
        if ("text" in event && event.text) await this.say(event.text);
        break;
    }
    this.refreshStatus();
  }

  /** Battle narration: types fast, clears itself, and can be hurried with Z. */
  private say(text: string): Promise<void> {
    return this.game.ui.message.show(text, { speed: 110, autoAdvance: 1.0 });
  }

  private async animateApproach(actor: Combatant, target: Combatant): Promise<void> {
    const attacker = this.viewOf(actor);
    if (!attacker) return;
    const object = attacker.object3D;
    const home = this.homeOf(actor);
    const targetPos = this.viewOf(target)?.object3D.position;
    if (!home || !targetPos) return;

    // Step most of the way to the target, swing, then step back.
    const direction = new THREE.Vector3().subVectors(targetPos, home).normalize();
    const lunge = home.clone().addScaledVector(direction, Math.max(0.7, home.distanceTo(targetPos) - 1.2));

    const model = attacker.model;
    await this.tweener.run(
      0.16,
      (t) => {
        object.position.lerpVectors(home, lunge, t);
        if (model instanceof CharacterModel) model.setSwing(t * 0.6);
      },
      Ease.quadOut,
    );
    if (model instanceof CharacterModel) model.setSwing(0);
    await this.tweener.run(
      0.2,
      (t) => {
        object.position.lerpVectors(lunge, home, t);
      },
      Ease.quadIn,
    );
  }

  private hitReaction(target: Combatant, damage: number, critical: boolean): void {
    const view = this.viewOf(target);
    if (!view) return;

    if (view.model instanceof MonsterModel) view.model.flash(critical ? 0.3 : 0.18);
    this.game.audio.play(this.isHero(target) ? "hurt" : "hit");
    this.shake = critical ? 0.4 : 0.22;

    if (damage > 0) this.popup(target, String(damage), critical ? "#ffd23f" : "#ff8d8d");

    // Knock the model back a little and let it spring home.
    const object = view.object3D;
    const home = this.homeOf(target);
    if (!home) return;
    const away = this.isHero(target) ? 0.28 : -0.28;
    void this.tweener.run(
      0.28,
      (t) => {
        object.position.z = home.z + Math.sin(t * Math.PI) * away;
      },
      Ease.linear,
    );
  }

  private async castFlare(actor: Combatant, skill: SkillDef): Promise<void> {
    const view = this.viewOf(actor);
    if (!view) return;

    if (view.model instanceof CharacterModel) view.model.setSwing(0.5);

    const color = skill.color ?? 0x88ddff;
    const flare = new THREE.Mesh(
      new THREE.SphereGeometry(0.3, 10, 8),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9 }),
    );
    flare.position.copy(view.object3D.position).add(new THREE.Vector3(0, 1, 0));
    this.scene.add(flare);

    await this.tweener.run(0.35, (t) => {
      flare.scale.setScalar(0.6 + t * 2.4);
      (flare.material as THREE.MeshBasicMaterial).opacity = 0.9 * (1 - t);
    });

    this.scene.remove(flare);
    flare.geometry.dispose();
    (flare.material as THREE.Material).dispose();
    if (view.model instanceof CharacterModel) view.model.setSwing(0);
  }

  private async animateDefeat(target: Combatant): Promise<void> {
    const view = this.viewOf(target);
    if (!view) return;
    this.game.audio.play("defeat");

    if (view.model instanceof MonsterModel) {
      const model = view.model;
      await this.tweener.run(0.45, (t) => {
        model.setOpacity(1 - t);
        model.object3D.position.y = -t * 0.5;
        model.object3D.rotation.z = t * 0.8;
      });
      model.object3D.visible = false;
    } else {
      const object = view.object3D;
      await this.tweener.run(0.4, (t) => {
        object.rotation.x = -t * 1.4;
        object.position.y = -t * 0.1;
      });
    }
  }

  // ---------------------------------------------------------------- lookups

  private isHero(c: Combatant): boolean {
    return this.heroViews.some((v) => v.hero === c);
  }

  private viewOfMonster(c: Combatant): MonsterView | undefined {
    return this.monsterViews.find((v) => v.monster === c);
  }

  private viewOf(c: Combatant): { object3D: THREE.Object3D; model: CharacterModel | MonsterModel } | null {
    const monster = this.monsterViews.find((v) => v.monster === c);
    if (monster) return { object3D: monster.model.object3D, model: monster.model };
    const hero = this.heroViews.find((v) => v.hero === c);
    if (hero) return { object3D: hero.model.object3D, model: hero.model };
    return null;
  }

  private homeOf(c: Combatant): THREE.Vector3 | null {
    return (
      this.monsterViews.find((v) => v.monster === c)?.home ??
      this.heroViews.find((v) => v.hero === c)?.home ??
      null
    );
  }

  private heightOf(c: Combatant): number {
    const monster = this.monsterViews.find((v) => v.monster === c);
    if (monster) return monster.model.height * (monster.monster.def.appearance.scale ?? 1);
    return 1.1;
  }

  // ----------------------------------------------------------------- chrome

  private highlightActor(hero: Hero | null): void {
    this.statusWindow.el.querySelectorAll("tr").forEach((row) => {
      row.classList.toggle("acting", hero !== null && row.dataset.name === hero.name);
    });
  }

  private refreshStatus(): void {
    const rows = this.battle.heroes
      .map((hero) => {
        const low = hero.hp <= hero.maxHp * 0.25;
        return (
          `<tr data-name="${hero.name}" class="${hero.alive ? "" : "dead"}">` +
          `<td>${hero.name}</td>` +
          `<td>Lv${hero.level}</td>` +
          `<td class="${low ? "ds-hp-low" : ""}">HP ${hero.hp}/${hero.maxHp}</td>` +
          `<td>MP ${hero.mp}/${hero.maxMp}</td></tr>`
        );
      })
      .join("");
    this.statusWindow.setHTML(`<table>${rows}</table>`).show();
  }

  // ---------------------------------------------------------- damage popups

  private popup(target: Combatant, text: string, color: string): void {
    const view = this.viewOf(target);
    if (!view) return;

    const el = document.createElement("div");
    el.textContent = text;
    el.style.cssText =
      `position:absolute;color:${color};font-family:var(--font);font-size:calc(var(--u) * 1.6);` +
      `text-shadow:0 2px 0 #000;pointer-events:none;transform:translate(-50%,-50%);`;
    this.game.ui.root.appendChild(el);

    const position = view.object3D.position.clone();
    position.y = this.heightOf(target) + 0.3;
    this.popups.push({ el, position, life: 0 });
  }

  private updatePopups(dt: number): void {
    if (this.popups.length === 0) return;
    const canvas = this.game.renderer.domElement;
    const rect = canvas.getBoundingClientRect();

    this.popups = this.popups.filter((popup) => {
      popup.life += dt;
      if (popup.life > 0.9) {
        popup.el.remove();
        return false;
      }
      const projected = popup.position.clone();
      projected.y += popup.life * 0.9;
      projected.project(this.camera);
      popup.el.style.left = `${rect.left + ((projected.x + 1) / 2) * rect.width}px`;
      popup.el.style.top = `${rect.top + ((1 - projected.y) / 2) * rect.height}px`;
      popup.el.style.opacity = String(Math.max(0, 1 - popup.life / 0.9));
      return true;
    });
  }
}
